import React from 'react';
import { useTranslation } from 'react-i18next';
import { MCQItem } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: MCQItem[];
    teacherMode: boolean;
    onWordAnswered?: (word: string, correct: boolean, hintUsed?: boolean) => void;
    sourceLanguage?: string;
    targetLanguage?: string;
}

const MCQSection: React.FC<Props> = ({ lessonId, items, teacherMode, onWordAnswered, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [selected, setSelected] = useLessonPersistedState<Record<number, number>>(lessonId, 'mcq.selected', {});
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'mcq.showTranslation', {});
    const [showExampleTranslation, setShowExampleTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'mcq.showExampleTranslation', {});
    const [explanationFallback, setExplanationFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'mcq.explanationFallback', {});
    const [exampleFallback, setExampleFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'mcq.exampleFallback', {});
    // Pre-answer icons (stem, options) can hint at the answer — opening one before the item is
    // answered flags it for a reduced score at Finish.
    const [showStemTranslation, setShowStemTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'mcq.showStemTranslation', {});
    const [stemFallback, setStemFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'mcq.stemFallback', {});
    const [showOptionsTranslation, setShowOptionsTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'mcq.showOptionsTranslation', {});
    const [optionsFallback, setOptionsFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'mcq.optionsFallback', {});
    const [hintUsed, setHintUsed] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'mcq.hintUsed', {});

    const markHint = (qi: number) => {
        if (selected[qi] !== undefined) return;
        setHintUsed((prev) => (prev[qi] ? prev : { ...prev, [qi]: true }));
    };

    // No "Check Answers" step — each question reveals correct/incorrect the instant it's
    // answered (same immediate-feedback pattern as DiscoveryCardsSection), so there's nothing to
    // persist beyond `selected` itself; the score is just derived from it on every render.
    const choose = (qi: number, oi: number) => {
        if (selected[qi] !== undefined) return;
        setSelected((prev) => ({ ...prev, [qi]: oi }));
        const item = items[qi];
        onWordAnswered?.(item.options[item.correct_option_index], oi === item.correct_option_index, hintUsed[qi]);
    };

    const answeredCount = Object.keys(selected).length;
    const correctCount = items.reduce((n, item, i) => (selected[i] === item.correct_option_index ? n + 1 : n), 0);

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.mcq.kicker', 'Practice')}</div>
                <h2>{t('sections.mcq.title', 'Choose the Right Option')}</h2>
                <p className="lesson-panel-desc">{t('sections.mcq.desc', 'Pick an option to see if it\'s right.')}</p>
            </div>
            <div className="lesson-qgrid">
                {items.map((item, i) => {
                    const revealed = selected[i] !== undefined;
                    return (
                        <div className="lesson-qcard" key={i}>
                            <div className="lesson-qnum">{t('sections.question_n', 'Question {{n}}', { n: i + 1 })}</div>
                            <div className="lesson-word-target">
                                {item.stem}
                                <TranslateToggle
                                    text={item.stem}
                                    fallback={stemFallback[i]}
                                    onFallbackFetched={(text) => setStemFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showStemTranslation[i]}
                                    onToggle={() => { markHint(i); setShowStemTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            <div className="lesson-opt-row">
                                {item.options.map((opt, oi) => {
                                    let cls = 'lesson-opt';
                                    if (revealed) {
                                        if (oi === item.correct_option_index) cls += ' correct';
                                        else if (oi === selected[i]) cls += ' incorrect';
                                    }
                                    return (
                                        <button key={oi} className={cls} disabled={revealed} onClick={() => choose(i, oi)}>
                                            {opt}
                                        </button>
                                    );
                                })}
                                <TranslateToggle
                                    text={item.options.join(' / ')}
                                    fallback={optionsFallback[i]}
                                    onFallbackFetched={(text) => setOptionsFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showOptionsTranslation[i]}
                                    onToggle={() => { markHint(i); setShowOptionsTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            {(revealed || teacherMode) && (
                                <div className="lesson-reveal lesson-word-target">
                                    <div className="lesson-reveal-explanation">
                                        <strong>{item.explanation}</strong>
                                        <TranslateToggle
                                            text={item.explanation}
                                            native={item.explanation_native}
                                            fallback={explanationFallback[i]}
                                            onFallbackFetched={(text) => setExplanationFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showTranslation[i]}
                                            onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                    <div>
                                        {t('sections.mcq.natural_example', 'e.g. {{example}}', { example: item.natural_example })}
                                        <TranslateToggle
                                            text={item.natural_example}
                                            native={item.natural_example_native}
                                            fallback={exampleFallback[i]}
                                            onFallbackFetched={(text) => setExampleFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showExampleTranslation[i]}
                                            onToggle={() => setShowExampleTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {answeredCount > 0 && (
                <div className="lesson-actions-row">
                    <div className="lesson-score-pill">{t('sections.score', '{{score}} / {{total}} correct', { score: correctCount, total: answeredCount })}</div>
                </div>
            )}
        </div>
    );
};

export default MCQSection;
